import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { loadConsent, trackGaEvent } from '@/lib/analytics';
import { getFirstTouchCapturedAt, getFirstTouchPayload } from '@/lib/firstTouch';

/**
 * Serverseitig idempotente Erfassung der E-Mail-Bestätigung (`email_confirmed`)
 * plus GA4 `sign_up` – letzteres nur bei Analytics-Einwilligung und nur beim
 * ersten Mal (der RPC liefert nur einmal `true`).
 *
 * Zusätzlich werden die First-Touch-Daten nachgetragen, sobald eine
 * Einwilligung erteilt wurde und ein Benutzer angemeldet ist.
 * Tracking darf die App nie blockieren – alle Fehler werden verschluckt.
 */
export const RegistrationTrackingSync = () => {
  const { user } = useAuth();
  const confirmTried = useRef(false);

  useEffect(() => {
    if (!user?.id || !user.email_confirmed_at || confirmTried.current) return;
    confirmTried.current = true;
    (async () => {
      try {
        const { data, error } = await (supabase as any).rpc('track_email_confirmed');
        if (!error && data === true && loadConsent()?.analytics) {
          trackGaEvent('sign_up', { method: 'email' });
        }
      } catch {
        /* noop */
      }
    })();
  }, [user?.id, user?.email_confirmed_at]);

  useEffect(() => {
    if (!user?.id) return;

    // Ein First Touch darf einem Konto nur dann als Herkunft zugeordnet
    // werden, wenn er zeitlich VOR (oder unmittelbar um) der Registrierung
    // erfasst wurde. Sonst würde bei Bestandsnutzern der heutige Besuch als
    // Akquisitionsquelle gespeichert – lieber leer lassen.
    const GRACE_MS = 10 * 60 * 1000;

    // Rollout-Cutoff der neuen Registrierungsmessung. Die erste Tracking-
    // Migration lief am 04.09.2026 um 15:56:11 UTC; konservativ wird 16:00 UTC
    // angesetzt. Konten, die davor angelegt wurden, koennen keinen echten
    // First Touch besitzen – fuer sie werden nie Herkunftsdaten gesendet.
    // Dieselbe Grenze gilt zusaetzlich serverseitig.
    const TRACKING_ROLLOUT_AT = Date.parse('2026-09-04T16:00:00Z');

    const isTouchAttributable = (): boolean => {
      const capturedAt = getFirstTouchCapturedAt();
      const createdAt = user.created_at;
      if (!capturedAt || !createdAt) return false;
      const c = Date.parse(capturedAt);
      const r = Date.parse(createdAt);
      if (!Number.isFinite(c) || !Number.isFinite(r)) return false;
      if (r < TRACKING_ROLLOUT_AT) return false;
      return c <= r + GRACE_MS;
    };

    const sync = () => {
      const consent = loadConsent();
      if (!consent) return;
      (async () => {
        try {
          const payload = getFirstTouchPayload();
          const attr = isTouchAttributable()
            ? payload
            : {
                analytics_consent: payload.analytics_consent,
                marketing_consent: payload.marketing_consent,
              };
          await (supabase as any).rpc('upsert_registration_attribution', {
            p_attr: attr,
          });
        } catch {
          /* noop */
        }
      })();
    };

    sync();
    window.addEventListener('aimup-cookie-consent-changed', sync);
    return () => window.removeEventListener('aimup-cookie-consent-changed', sync);
  }, [user?.id]);

  return null;
};

export default RegistrationTrackingSync;
